//  ----------      START   IMPORT  BLOCK       ----------
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
//  Services
import UserService from '../Services/UserService'; 
import AuthService from '../Services/AuthService';
//  Context
import { useUser } from './UserContext'; 
import { useApplicationUI } from './AppUIContext';
import { UserRole } from '../../Types/User/UserRoles';
import type { UserRead } from '../../Types/schema-helpers';
//  ----------      END   IMPORT  BLOCK       ----------

interface SessionContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    loginSuccess: (user: UserRead, isProfessionalPreference: boolean) => void;
    logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType | null>(null);


export const useSession = (): SessionContextType => {
    const context = useContext(SessionContext);
    if (!context) throw new Error("useSession must be used within SessionProvider");
    return context;
};

export const useAuthFlow = () => {
    const session = useSession();
    
    const handleLoginFlow = async (
        username: string, 
        password: string, 
        isProfessionalPreference: boolean
    ): Promise<boolean> => {
        try {
            // Called directly on AuthService (static method)
            const response = await AuthService.login(username, password);

            if (response.success && response.user) {
                session.loginSuccess(response.user, isProfessionalPreference); 
                return true;
            }
            return false; 
        } catch (error) {
            console.error("Authentication Error in useAuthFlow:", error);
            throw error; 
        }
    };
    
    return { handleLoginFlow, logout: session.logout };
};

export const SessionProvider = ({ children }: { children: ReactNode }) => {
    const { setProfile, clearProfile } = useUser(); 
    const { toggleProfessionalDashboard } = useApplicationUI(); 
    
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true); 

    const isMedicUser = (user: UserRead | null): boolean => {
        if (!user || !user.roles) return false;

        const medicRoles: string[] = [
            UserRole.PENDINGPROFESSIONAL, 
            UserRole.VERIFYINGPROFESSIONAL, 
            UserRole.PROFESSIONAL,
            "medic",
            "doctor",
            "professional"
        ];

        return user.roles.some(role => medicRoles.includes(role));
    };

    const handleAuthFailure = () => {
        clearProfile();
        setIsAuthenticated(false);
        localStorage.removeItem("session_exists");
    };

    useEffect(() => {
        const sessionFlag = localStorage.getItem("session_exists");
        if (!sessionFlag) {
            setIsLoading(false);
            return;
        }

        const checkAuthStatus = async () => {
            try {
                const checkedUser = await UserService.fetchCurrentUser(); 
                if (checkedUser) {
                    setProfile((prevUser: UserRead | null): UserRead => {
                        if (!prevUser) return checkedUser;

                        return {
                            ...prevUser,
                            ...checkedUser,
                            questionnaire_status: checkedUser.questionnaire_status || prevUser.questionnaire_status,
                            roles: checkedUser.roles?.length ? checkedUser.roles : prevUser.roles
                        };
                    });

                    setIsAuthenticated(true);
                    
                    const shouldSeePro = isMedicUser(checkedUser);
                    toggleProfessionalDashboard(shouldSeePro);
                } else {
                    handleAuthFailure();
                }
            } catch (error) {
                console.error("Auth status verification failed:", error);
                handleAuthFailure();
            } finally {
                setIsLoading(false); 
            }
        };

        checkAuthStatus();
    }, [setProfile, toggleProfessionalDashboard]);

    const loginSuccess = (user: UserRead, isProfessionalPreference: boolean) => {
        setProfile(user); 
        const canViewPro = isMedicUser(user);
        
        toggleProfessionalDashboard(isProfessionalPreference && canViewPro);
        
        setIsAuthenticated(true);
        setIsLoading(false);
        localStorage.setItem("session_exists", "true"); 
    };

    const logout = async () => {
        try {
            // Called directly on AuthService (static method)
            await AuthService.logout();
        } catch (error) {
            console.warn("Logout failed on server, clearing client session anyway:", error);
        } finally {
            clearProfile(); 
            setIsAuthenticated(false);
            localStorage.removeItem("session_exists");
        }
    };

    const value = useMemo(() => ({
        isAuthenticated,
        isLoading,
        loginSuccess,
        logout,
    }), [isAuthenticated, isLoading]);

    return (
        <SessionContext.Provider value={value}>
            {children}
        </SessionContext.Provider>
    );
};